import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { supabase } from "../lib/supabase";
import MetricsBar from "./MetricsBar";

interface NavCounts {
  unread: number;
  reviews: number;
}

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  badge?: number;
}

/**
 * App chrome: left nav (bottom tab bar on phones), MetricsBar header, and the
 * routed page in an <Outlet/>. Unread-inbox and open-review counts poll every
 * 30 seconds; they are cosmetic, so failures are swallowed.
 */
export function Layout() {
  const [counts, setCounts] = useState<NavCounts>({ unread: 0, reviews: 0 });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [m, r] = await Promise.all([
          supabase
            .from("messages")
            .select("id", { count: "exact", head: true })
            .eq("direction", "in")
            .is("read_at", null),
          supabase
            .from("needs_review")
            .select("id", { count: "exact", head: true })
            .eq("status", "open"),
        ]);
        if (!cancelled) setCounts({ unread: m.count ?? 0, reviews: r.count ?? 0 });
      } catch {
        // Badge counts are cosmetic; never break the shell over them.
      }
    }
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const items: NavItem[] = [
    { to: "/", label: "Home", end: true },
    { to: "/clients", label: "Clients" },
    { to: "/inbox", label: "Inbox", badge: counts.unread },
    { to: "/review", label: "Review", badge: counts.reviews },
    { to: "/segments", label: "Segments" },
    { to: "/campaigns", label: "Campaigns" },
    { to: "/marketing", label: "Marketing" },
    { to: "/referrals", label: "Referrals" },
    { to: "/ads", label: "Ads" },
    { to: "/settings", label: "Settings" },
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          Fresh IV <span className="brand-accent">CRM</span>
        </div>
        <nav className="nav" aria-label="Primary">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              <span className="nav-label">{it.label}</span>
              {it.badge != null && it.badge > 0 && (
                <span className="nav-badge">{it.badge > 99 ? "99+" : it.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="main">
        <MetricsBar />
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
